import { useState, useMemo, useEffect } from 'react';
import { MyHtxSection } from './MyHtxSection';
import type { Farmer } from '@/types';
import type { FarmerKycInfo } from '@/store';
import { StatusBadge } from '@/components/base/StatusBadge';
import { showToast } from '@/components/base/Toast';
import { updateFarmer, addEditHistory, respondToHtxInvite, leaveHtx } from '@/store';
import { formatDate, formatCompactCurrency } from '@/utils/format';
import { confirmModal } from '@/components/base/ConfirmModal';
import { getKycLabel } from '@/store';
import { getKycColor } from '@/utils/status';
import { getFarmerMoneySummary } from '@/store';
import { useAuth } from '@/hooks/useAuth';
import { useCategories } from '@/hooks/useCategories';
import { ProfileHistoryPanel } from '@/components/feature/ProfileHistoryPanel';
import { FarmerAttachmentsSection } from '@/components/feature/FarmerAttachmentsSection';
import { PROVINCE_FALLBACK } from '@/constants/categoryFallbacks';
import { listHtx, type BackendHtx } from '@/api/ekyc';

interface ProfileTabProps {
  farmer: Farmer;
  kyc: FarmerKycInfo;
}

export default function ProfileTab({ farmer, kyc }: ProfileTabProps) {
  const { user: currentUser } = useAuth();
  const { names: provinceNames } = useCategories('PROVINCE', PROVINCE_FALLBACK);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Farmer>({ ...farmer });

  const isVerified = kyc.kycStatus === 'ekyc_verified';
  const kycColor = getKycColor(kyc.kycStatus);

  // Not memoized — see overview/page.tsx's note on why memoizing this on
  // farmerId alone freezes a stale (often all-zero) first-render snapshot.
  const moneySummary = getFarmerMoneySummary(farmer.id);

  const [htxList, setHtxList] = useState<BackendHtx[]>([]);
  const [membershipBusy, setMembershipBusy] = useState(false);
  useEffect(() => {
    listHtx().then(setHtxList).catch(() => setHtxList([]));
  }, []);
  const currentHtxName = useMemo(
    () => (farmer.htxId ? htxList.find((h) => h._id === farmer.htxId)?.name : undefined),
    [htxList, farmer.htxId],
  );
  const hasPendingHtxInvite = farmer.membershipInitiatedBy === 'HTX' && farmer.membershipStatus === 'Chờ xác nhận';

  const handleAcceptInvite = async () => {
    setMembershipBusy(true);
    try {
      await respondToHtxInvite(farmer.id, 'ACCEPT');
      showToast('Đã tham gia hợp tác xã', 'success');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Không thể xác nhận lời mời', 'error');
    } finally {
      setMembershipBusy(false);
    }
  };

  const handleDeclineInvite = async () => {
    setMembershipBusy(true);
    try {
      await respondToHtxInvite(farmer.id, 'DECLINE');
      showToast('Đã từ chối lời mời', 'info');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Không thể từ chối lời mời', 'error');
    } finally {
      setMembershipBusy(false);
    }
  };

  const handleLeaveHtx = async () => {
    const ok = await confirmModal({
      title: 'Rời hợp tác xã?',
      message: `Bạn sẽ không còn thuộc "${currentHtxName || 'hợp tác xã hiện tại'}". Các mùa vụ đang hoạt động sẽ chuyển về dạng độc lập — hợp đồng và khoản vay đã có vẫn được giữ nguyên.`,
      confirmText: 'Rời hợp tác xã',
      cancelText: 'Ở lại',
      variant: 'danger',
    });
    if (!ok) return;
    setMembershipBusy(true);
    try {
      await leaveHtx(farmer.id);
      showToast('Bạn đã rời hợp tác xã — tài khoản chuyển sang hoạt động độc lập', 'success');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Không thể rời hợp tác xã', 'error');
    } finally {
      setMembershipBusy(false);
    }
  };

  const handleChange = (field: keyof Farmer, value: string | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    const ok = await confirmModal({
      title: 'Xác nhận cập nhật',
      message: 'Bạn có chắc muốn lưu thay đổi hồ sơ nông hộ?',
      confirmText: 'Lưu thay đổi',
      cancelText: 'Hủy',
      variant: 'primary',
    });
    if (!ok) return;

    const changedFields: (keyof Farmer)[] = [
      'name', 'phone', 'address', 'tinhThanh', 'huyen', 'xa',
      'soThanhVien', 'loaiCay', 'ghiChu',
    ];

    changedFields.forEach((field) => {
      const oldVal = String(farmer[field] ?? '');
      const newVal = String(form[field] ?? '');
      if (oldVal !== newVal) {
        addEditHistory({
          entityType: 'Farmer',
          entityId: farmer.id,
          farmerId: farmer.id,
          truong: field,
          giaTriCu: oldVal,
          giaTriMoi: newVal,
          ngaySua: new Date().toISOString(),
          nguoiSua: currentUser?.displayName || 'Nông hộ',
        });
      }
    });

    updateFarmer(farmer.id, {
      name: form.name,
      phone: form.phone,
      address: form.address,
      tinhThanh: form.tinhThanh,
      huyen: form.huyen,
      xa: form.xa,
      soThanhVien: form.soThanhVien,
      loaiCay: form.loaiCay,
      ghiChu: form.ghiChu,
    });
    setEditing(false);
    showToast('Đã cập nhật hồ sơ nông hộ', 'success');
  };

  const handleCancel = () => {
    setForm({ ...farmer });
    setEditing(false);
  };

  const displayFields = [
    { label: 'Mã nông hộ', value: farmer.code || 'Chưa có mã', editable: false },
    { label: 'Họ tên chủ hộ', value: farmer.name, field: 'name' as const },
    { label: 'Số điện thoại', value: farmer.phone, field: 'phone' as const },
    { label: 'Địa chỉ', value: farmer.address, field: 'address' as const },
    { label: 'Tỉnh/thành', value: farmer.tinhThanh, field: 'tinhThanh' as const },
    { label: 'Huyện', value: farmer.huyen, field: 'huyen' as const },
    { label: 'Xã', value: farmer.xa, field: 'xa' as const },
    { label: 'Số thành viên lao động', value: String(farmer.soThanhVien), field: 'soThanhVien' as const, type: 'number' },
    { label: 'Cây trồng chính', value: farmer.loaiCay, field: 'loaiCay' as const },
    { label: 'Ngày bắt đầu hợp tác', value: formatDate(farmer.ngayBatDau), editable: false },
    { label: 'Trạng thái', value: <StatusBadge status={farmer.status} />, editable: false },
    { label: 'Ghi chú', value: farmer.ghiChu || '-', field: 'ghiChu' as const },
  ];

  return (
    <div className="space-y-4">
      <MyHtxSection />
      {/* Card hồ sơ */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="h-24 bg-gradient-to-r from-agrida-600 to-agrida-700 flex items-center px-5">
          <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center shrink-0 mr-4">
            <i className="ri-user-3-line text-2xl text-white" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-base text-white">{farmer.name}</p>
            <p className="text-xs text-agrida-100">{farmer.code}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${kycColor.bg} ${kycColor.text}`}>
                {getKycLabel(kyc.kycStatus)}
              </span>
              {kyc.creditScore !== undefined && (
                <span className="text-[10px] text-agrida-200 font-medium">Score: {kyc.creditScore}</span>
              )}
            </div>
          </div>
        </div>
        <div className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h3 className="text-sm font-semibold text-slate-900">Thông tin chi tiết</h3>
            {!editing && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="px-3 py-2 rounded-xl text-xs font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[44px]"
              >
                <span className="w-3.5 h-3.5 inline-flex items-center justify-center mr-1">
                  <i className="ri-edit-line" />
                </span>
                Chỉnh sửa
              </button>
            )}
          </div>

          {editing ? (
            <div className="space-y-3">
              {displayFields.map((f, idx) => {
                if (!f.editable && f.editable !== undefined) {
                  return (
                    <div key={idx}>
                      <p className="text-[11px] text-slate-400 mb-0.5">{f.label}</p>
                      <div className="text-sm font-medium text-slate-800">{f.value}</div>
                    </div>
                  );
                }
                const fieldName = f.field!;
                const val = form[fieldName] ?? '';
                return (
                  <div key={idx}>
                    <label className="block text-[11px] text-slate-400 mb-0.5">{f.label}</label>
                    {fieldName === 'ghiChu' ? (
                      <textarea
                        value={String(val)}
                        onChange={(e) => handleChange(fieldName, e.target.value)}
                        rows={3}
                        className="w-full resize-none px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500 focus:border-transparent"
                      />
                    ) : fieldName === 'tinhThanh' ? (
                      <select
                        value={String(val)}
                        onChange={(e) => handleChange(fieldName, e.target.value)}
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500 focus:border-transparent"
                      >
                        <option value="">— Chọn tỉnh/thành —</option>
                        {provinceNames.map((p) => (<option key={p} value={p}>{p}</option>))}
                      </select>
                    ) : (
                      <input
                        type={f.type || 'text'}
                        value={val}
                        onChange={(e) =>
                          handleChange(fieldName, f.type === 'number' ? Number(e.target.value) : e.target.value)
                        }
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500 focus:border-transparent"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {displayFields.map((f, idx) => {
                const isNote = f.field === 'ghiChu';
                return (
                  <div key={idx} className={`${isNote ? 'block' : 'flex items-center justify-between'} py-2.5 first:pt-0 last:pb-0`}>
                    <p className="text-xs text-slate-500">{f.label}</p>
                    <div className={`${isNote ? 'mt-1 rounded-xl bg-slate-50 px-3 py-2 text-left leading-relaxed text-slate-600' : 'max-w-[60%] text-right text-slate-800'} text-xs font-medium`}>
                      {f.value}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {editing && (
            <div className="flex flex-wrap gap-2 justify-end mt-4">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 rounded-xl text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[44px]"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="px-4 py-2 rounded-xl text-xs font-medium bg-agrida-600 hover:bg-agrida-700 text-white transition-colors whitespace-nowrap min-h-[44px]"
              >
                Lưu thay đổi
              </button>
            </div>
          )}

          {/* The same document checklist the HTX sees for this farmer — the farmer
              manages their own documents here. */}
          <FarmerAttachmentsSection farmer={farmer} editable />

          <div className="mt-4 pt-4 border-t border-slate-100">
            <ProfileHistoryPanel farmerId={farmer.id} />
          </div>
        </div>
      </div>

      {/* ─── HTX membership card ─── */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-100 px-4 py-3">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <span className="flex h-5 w-5 items-center justify-center text-agrida-600"><i className="ri-community-line" /></span>
            Hợp tác xã
          </h3>
        </div>
        <div className="p-4">
          {hasPendingHtxInvite ? (
            <div className="rounded-xl border border-sky-100 bg-sky-500/5 p-3">
              <p className="text-sm font-semibold text-slate-800">Lời mời tham gia HTX</p>
              <p className="mt-0.5 text-xs text-slate-500">
                {currentHtxName || 'Một hợp tác xã'} đã mời bạn tham gia. Xác nhận để bắt đầu sử dụng hợp đồng, hạn mức và các hỗ trợ từ HTX.
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={handleDeclineInvite}
                  disabled={membershipBusy}
                  className="flex-1 rounded-xl border border-slate-200 py-2 text-xs font-semibold text-slate-600 disabled:opacity-50"
                >
                  Từ chối
                </button>
                <button
                  type="button"
                  onClick={handleAcceptInvite}
                  disabled={membershipBusy}
                  className="flex-1 rounded-xl bg-agrida-600 py-2 text-xs font-semibold text-white disabled:opacity-50"
                >
                  Xác nhận tham gia
                </button>
              </div>
            </div>
          ) : farmer.htxId ? (
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-800">{currentHtxName || 'Đang tải...'}</p>
                <p className="mt-0.5 text-xs text-slate-500">
                  {farmer.membershipStatus === 'Đã xác nhận' ? 'Thành viên chính thức' : 'Đang chờ HTX xác nhận'}
                </p>
              </div>
              {farmer.membershipStatus === 'Đã xác nhận' && (
                <button
                  type="button"
                  onClick={handleLeaveHtx}
                  disabled={membershipBusy}
                  className="shrink-0 rounded-xl border border-red-100 px-3 py-2 text-xs font-semibold text-red-500 disabled:opacity-50"
                >
                  Rời HTX
                </button>
              )}
            </div>
          ) : (
            <p className="text-xs text-slate-500">
              Bạn đang hoạt động độc lập, chưa thuộc hợp tác xã nào. Nếu một HTX mời bạn tham gia, lời mời sẽ hiển thị ở đây để bạn xác nhận.
            </p>
          )}
        </div>
      </div>

      {/* ─── Money Stats Card ─── */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center text-agrida-600"><i className="ri-funds-line" /></span>
            Hạn mức & Tài chính
          </h3>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-sky-50 rounded-xl p-2.5 text-center">
              <p className="text-[10px] text-sky-500 mb-0.5">Tổng hạn mức</p>
              <p className="text-sm font-bold text-sky-800">{formatCompactCurrency(moneySummary.hanMucTienTong)}</p>
            </div>
            <div className="bg-orange-50 rounded-xl p-2.5 text-center">
              <p className="text-[10px] text-orange-500 mb-0.5">Đã dùng</p>
              <p className="text-sm font-bold text-orange-700">{formatCompactCurrency(moneySummary.hanMucTienDaSuDungThucTe)}</p>
            </div>
            <div className={`rounded-xl p-2.5 text-center ${moneySummary.hanMucTienKhaDung > 0 ? 'bg-agrida-50' : 'bg-red-50'}`}>
              <p className={`text-[10px] mb-0.5 ${moneySummary.hanMucTienKhaDung > 0 ? 'text-agrida-500' : 'text-red-500'}`}>Khả dụng</p>
              <p className={`text-sm font-bold ${moneySummary.hanMucTienKhaDung > 0 ? 'text-agrida-700' : 'text-red-700'}`}>{formatCompactCurrency(moneySummary.hanMucTienKhaDung)}</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className={`rounded-xl p-2.5 text-center ${moneySummary.duNo > 0 ? 'bg-red-50' : 'bg-slate-50'}`}>
              <p className="text-[10px] text-slate-500 mb-0.5">Dư nợ</p>
              <p className={`text-sm font-bold ${moneySummary.duNo > 0 ? 'text-red-700' : 'text-slate-500'}`}>{formatCompactCurrency(moneySummary.duNo)}</p>
            </div>
            <div className={`rounded-xl p-2.5 text-center ${moneySummary.choGiaiNgan > 0 ? 'bg-amber-50' : 'bg-slate-50'}`}>
              <p className="text-[10px] text-slate-500 mb-0.5">Đang khóa</p>
              <p className={`text-sm font-bold ${moneySummary.choGiaiNgan > 0 ? 'text-amber-700' : 'text-slate-500'}`}>{formatCompactCurrency(moneySummary.hanMucTienDangKhoa)}</p>
            </div>
            <div className={`rounded-xl p-2.5 text-center ${moneySummary.quaHan > 0 ? 'bg-red-50' : 'bg-slate-50'}`}>
              <p className={`text-[10px] mb-0.5 ${moneySummary.quaHan > 0 ? 'text-red-500' : 'text-slate-500'}`}>Quá hạn</p>
              <p className={`text-sm font-bold ${moneySummary.quaHan > 0 ? 'text-red-700' : 'text-slate-500'}`}>{moneySummary.quaHan} khoản</p>
            </div>
          </div>
          {/* Progress bar */}
          {moneySummary.hanMucTienTong > 0 && (
            <div>
              <div className="flex justify-between text-[10px] text-slate-400 mb-1">
                <span>Đã sử dụng</span>
                <span>{moneySummary.hanMucTienTong > 0 ? ((moneySummary.hanMucTienDaDung / moneySummary.hanMucTienTong) * 100).toFixed(1) : '0'}%</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                <div
                  className="h-full rounded-full bg-agrida-500 transition-all"
                  style={{ width: `${Math.min(100, (moneySummary.hanMucTienDaDung / moneySummary.hanMucTienTong) * 100)}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
